import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ApiError } from "@agentsaas/protocol";
import type { CallInput, CallOutput } from "@agentsaas/client-sdk";
import { useServices } from "../app/services";
import { useSession } from "../app/session";

/**
 * P5 管理控制台数据。
 * 契约里用户没有"平台管理员"标记：用 adminNodes 探测，403 = 不是管理员（结果缓存到会话结束，不重试）。
 */

export type NodeInfo = CallOutput<"adminNodes">["items"][number];
export type Placement = CallOutput<"adminPlacements">["items"][number];
export type AuditEntry = CallOutput<"adminAudit">["items"][number];
export type AuditFilter = Omit<NonNullable<CallInput<"adminAudit">["query"]>, "cursor" | "limit">;

export const ak = {
  probe: (uid: string) => ["admin", "probe", uid] as const,
  nodes: ["admin", "nodes"] as const,
  placements: (nodeId?: string) => ["admin", "placements", nodeId ?? ""] as const,
  placementsAll: ["admin", "placements"] as const,
  audit: (f: AuditFilter) => ["admin", "audit", f] as const,
  review: ["admin", "review"] as const,
};

/** 当前用户是否平台管理员：true / false / undefined（探测中） */
export function useIsPlatformAdmin(): boolean | undefined {
  const { api } = useServices();
  const uid = useSession().state.user?.id;
  const q = useQuery({
    queryKey: ak.probe(uid ?? ""),
    enabled: !!uid,
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      try {
        await api.call("adminNodes");
        return true;
      } catch (e) {
        if (e instanceof ApiError && (e.status === 403 || e.status === 404)) return false;
        throw e;
      }
    },
  });
  return q.data;
}

export function useNodes(enabled = true) {
  const { api } = useServices();
  return useQuery({ queryKey: ak.nodes, enabled, queryFn: async () => (await api.call("adminNodes")).items, refetchInterval: 10_000 });
}

export function usePlacements(nodeId?: string) {
  const { api } = useServices();
  return useQuery({ queryKey: ak.placements(nodeId), queryFn: async () => (await api.call("adminPlacements", { query: nodeId ? { nodeId: nodeId as never } : {} })).items });
}

export function useAudit(filter: AuditFilter) {
  const { api } = useServices();
  const q = useInfiniteQuery({
    queryKey: ak.audit(filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.call("adminAudit", { query: { ...filter, limit: 30, ...(pageParam ? { cursor: pageParam } : {}) } }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  return { ...q, items: q.data?.pages.flatMap((p) => p.items) ?? [] };
}

/** 审核队列：listingSearch（管理员能看到待审核 / 已下架，见 MEMORY 的契约缺口） */
export function useReviewQueue() {
  const { api } = useServices();
  return useQuery({ queryKey: ak.review, queryFn: async () => (await api.call("listingSearch", { query: { sort: "newest", limit: 100 } })).items });
}
